import {
  ApiOperationType,
  EntityIdentificationType,
  EntityListImportAction,
  EntityListResponse,
  EntityListSourceType,
  EntityListTypeName,
} from '@ydistri/api-sdk';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ActiveScreenType } from '../common/administrationItemsLib';
import {
  EntityListProductInputData,
  EntityListRow,
  EntityListSkuInputData,
  EntityListSkuQuantityInputData,
  EntityListTypeSupported,
  IMPORT_STEPS,
} from './entityListsLib';
import { WritableDraft } from 'immer';
import { ImportedItemRow } from '../../../lib/imports/importTypes';

export type EntityListImportStep = (typeof IMPORT_STEPS)[keyof typeof IMPORT_STEPS];
export type EntityListImportStepRunning = Record<EntityListImportStep, boolean>;
export const getNumericStep = (x: EntityListImportStep): number => parseInt(x.slice('step'.length));

type EntityListImportStateMap = Record<
  number,
  EntityListState<EntityListTypeSupported, EntityListProductInputData>
>;

export interface EntityListState<
  T extends EntityListTypeSupported,
  U extends EntityListProductInputData,
> {
  activeScreenType: ActiveScreenType;
  activeScreenTypeForced: boolean;
  productItemsContent: string;
  productItemsErrors: string;
  productIdentificationType: EntityIdentificationType;
  storeIdentificationType: EntityIdentificationType;
  parsedEntityListItems: EntityListRow<T>[];
  parsedImportedEntityListItems: ImportedItemRow<U>[];
  importStep: EntityListImportStep;
  importStepRunning: EntityListImportStepRunning;
  verificationTimeStamp?: string;
  selectedValidationImportAction?: EntityListImportAction;
  importType?: ApiOperationType;
}

export interface EntityListTypeAdministrationState {
  selectedEntityList?: EntityListResponse;
  showArchivedItems: boolean;
  sortField?: string;
  isLoading: boolean;
  imports: EntityListImportStateMap;
  sourceTypeFilters?: EntityListSourceType[];
}

const getInitialEntityListTypeState = (): EntityListTypeAdministrationState => ({
  selectedEntityList: undefined,
  showArchivedItems: false,
  sortField: 'LastUpdatedAt',
  isLoading: false,
  imports: {},
  sourceTypeFilters: [EntityListSourceType.User],
});
export const getInitialEntityListState = <
  T extends EntityListTypeSupported,
  U extends EntityListProductInputData,
>(): EntityListState<T, U> => {
  return {
    activeScreenType: ActiveScreenType.DETAIL,
    activeScreenTypeForced: false,
    productItemsContent: '',
    productItemsErrors: '',
    productIdentificationType: EntityIdentificationType.CustomerId,
    storeIdentificationType: EntityIdentificationType.CustomerId,
    parsedEntityListItems: [],
    parsedImportedEntityListItems: [],
    importStep: IMPORT_STEPS.ITEM_IMPORT,
    importStepRunning: {
      step1: false,
      step2: false,
    },
    verificationTimeStamp: undefined,
    selectedValidationImportAction: undefined,
  };
};

export const createEntityListState = (
  entityListType: EntityListTypeSupported,
): EntityListState<EntityListTypeSupported, EntityListProductInputData> => {
  switch (entityListType) {
    case EntityListTypeName.Product:
      return getInitialEntityListState<EntityListTypeName.Product, EntityListProductInputData>();
    case EntityListTypeName.Request:
      return getInitialEntityListState<
        EntityListTypeName.Request,
        EntityListSkuQuantityInputData
      >();
    case EntityListTypeName.MinLayer:
      return getInitialEntityListState<
        EntityListTypeName.MinLayer,
        EntityListSkuQuantityInputData
      >();
    case EntityListTypeName.Source:
      return getInitialEntityListState<EntityListTypeName.Source, EntityListSkuInputData>();
    case EntityListTypeName.Target:
      return getInitialEntityListState<EntityListTypeName.Target, EntityListSkuInputData>();
    case EntityListTypeName.Forced:
      return getInitialEntityListState<EntityListTypeName.Forced, EntityListSkuInputData>();
  }
};

export type EntityListAdministrationState = Record<
  EntityListTypeSupported,
  EntityListTypeAdministrationState
>;

const initialState: EntityListAdministrationState = {
  [EntityListTypeName.Product]: getInitialEntityListTypeState(),
  [EntityListTypeName.Request]: getInitialEntityListTypeState(),
  [EntityListTypeName.MinLayer]: getInitialEntityListTypeState(),
  [EntityListTypeName.Source]: getInitialEntityListTypeState(),
  [EntityListTypeName.Target]: getInitialEntityListTypeState(),
  [EntityListTypeName.Forced]: getInitialEntityListTypeState(),
};

const getEntityListState = (
  state: WritableDraft<EntityListAdministrationState>,
  entityListType: EntityListTypeSupported,
  entityListId: number,
) => {
  const imports = state[entityListType].imports;
  if (entityListId in imports) {
    return imports[entityListId];
  } else {
    return createEntityListState(entityListType);
  }
};

export interface EntityListStateUpdatePayloadAction {
  entityListType: EntityListTypeSupported;
  /** The list to update; the selected list when not given. */
  entityListId?: number;
}

function updateEntityListState(
  state: WritableDraft<EntityListAdministrationState>,
  action: PayloadAction<EntityListStateUpdatePayloadAction>,
  updateFn: (
    entityListState: EntityListState<EntityListTypeSupported, EntityListProductInputData>,
  ) => void,
): void {
  const entityListId =
    action.payload.entityListId ??
    state[action.payload.entityListType].selectedEntityList?.entityListId;
  if (entityListId === undefined) return;
  // A list named explicitly is updated only while it has a state; a reset (a project switch) removed it.
  if (
    action.payload.entityListId !== undefined &&
    !(entityListId in state[action.payload.entityListType].imports)
  ) {
    return;
  }

  const entityState = getEntityListState(state, action.payload.entityListType, entityListId);

  updateFn(entityState);

  state[action.payload.entityListType].imports[entityListId] = entityState;
}

const resetImportStateFields = (
  entityState: EntityListState<EntityListTypeSupported, EntityListProductInputData>,
): void => {
  delete entityState.importType;
  delete entityState.verificationTimeStamp;
  entityState.parsedEntityListItems = [];
  entityState.parsedImportedEntityListItems = [];
};

export const entityListAdministrationSlice = createSlice({
  name: 'entityListsAdministration',
  initialState,
  reducers: {
    setSelectedEntityList: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: EntityListResponse | undefined;
        openDetailPage?: boolean;
      }>,
    ) => {
      const elType = action.payload.entityListType;
      state[elType].selectedEntityList = action.payload.data;

      if (action.payload.data) {
        const entityListId = action.payload.data.entityListId;
        state[action.payload.entityListType].imports[entityListId] = getEntityListState(
          state,
          action.payload.entityListType,
          entityListId,
        );
      }
    },

    setActiveScreenType: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        entityListId?: number;
        data: ActiveScreenType;
        forced?: boolean;
      }>,
    ) => {
      updateEntityListState(state, action, entityListState => {
        entityListState.activeScreenType = action.payload.data;
        entityListState.activeScreenTypeForced = action.payload.forced ?? false;
      });
    },

    setShowArchivedItems: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: boolean;
      }>,
    ) => {
      state[action.payload.entityListType].showArchivedItems = action.payload.data;
    },

    setSortField: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: string;
      }>,
    ) => {
      state[action.payload.entityListType].sortField = action.payload.data;
    },
    setSourceTypeFilters: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: EntityListSourceType[];
      }>,
    ) => {
      state[action.payload.entityListType].sourceTypeFilters = action.payload.data;
    },
    setIsLoading: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: boolean;
      }>,
    ) => {
      state[action.payload.entityListType].isLoading = action.payload.data;
    },

    setImportStep: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: EntityListImportStep;
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.importStep = action.payload.data;
      });
    },

    setImportStepRunning: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: {
          step: EntityListImportStep;
          value: boolean;
        };
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.importStepRunning[action.payload.data.step] = action.payload.data.value;
      });
    },

    setVerificationTimeStamp: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data?: string;
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.verificationTimeStamp = action.payload.data;
      });
    },

    setSelectedValidationImportAction: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: EntityListImportAction;
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.selectedValidationImportAction = action.payload.data;
      });
    },

    setProductIdentificationType: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: EntityIdentificationType;
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.productIdentificationType = action.payload.data;
      });
    },

    setStoreIdentificationType: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: EntityIdentificationType;
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.storeIdentificationType = action.payload.data;
      });
    },
    setProductItemsError: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: string;
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.productItemsErrors = action.payload.data;
      });
    },
    setProductItemsContent: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        entityListId?: number;
        data: string;
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.productItemsContent = action.payload.data;
      });
    },

    resetEntityListItems: (
      state,
      action: PayloadAction<{ entityListType: EntityListTypeSupported; entityListId?: number }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.parsedEntityListItems = [];
        entityState.parsedImportedEntityListItems = [];
      });
    },

    setEntityListItems: <T extends EntityListTypeSupported>(
      state: WritableDraft<EntityListAdministrationState>,
      action: PayloadAction<{
        entityListType: T;
        data: EntityListRow<T>[];
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.parsedEntityListItems = action.payload.data;
      });
    },
    setImportedEntityListItems: <U extends EntityListProductInputData>(
      state: WritableDraft<EntityListAdministrationState>,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        data: readonly ImportedItemRow<U>[];
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.parsedImportedEntityListItems = [...action.payload.data];
      });
    },
    initImport: (
      state,
      action: PayloadAction<{
        entityListType: EntityListTypeSupported;
        verificationTimeStamp: string;
        importType: ApiOperationType;
      }>,
    ) => {
      updateEntityListState(state, action, entityState => {
        entityState.verificationTimeStamp = action.payload.verificationTimeStamp;
        entityState.importType = action.payload.importType;
      });
    },
    resetImport: (state, action: PayloadAction<{ entityListType: EntityListTypeSupported }>) => {
      updateEntityListState(state, action, entityState => {
        resetImportStateFields(entityState);
      });
    },

    /**
     * Resets import state for a given entity list across ALL type tabs.
     * Use after finalize/cancel to ensure other type tabs don't show stale import state.
     */
    resetImportForAllTypes: (state, action: PayloadAction<{ entityListId: number }>) => {
      const { entityListId } = action.payload;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- override
      const allTypes = Object.keys(state) as EntityListTypeSupported[];
      for (const typeName of allTypes) {
        const imports = state[typeName].imports;
        if (entityListId in imports) {
          const entityState = imports[entityListId];
          resetImportStateFields(entityState);
          entityState.importStep = IMPORT_STEPS.ITEM_IMPORT;
          entityState.importStepRunning = { step1: false, step2: false };
          entityState.activeScreenType = ActiveScreenType.DETAIL;
          entityState.activeScreenTypeForced = true;
          entityState.productItemsContent = '';
        }
      }
    },
    resetState: () => initialState,
  },
});

export const {
  setSelectedEntityList,
  setActiveScreenType,
  setShowArchivedItems,
  setSortField,
  setProductIdentificationType,
  setStoreIdentificationType,
  setIsLoading,
  setImportStep,
  setImportStepRunning,
  setVerificationTimeStamp,
  setSelectedValidationImportAction,
  setProductItemsContent,
  setProductItemsError,
  setEntityListItems,
  resetEntityListItems,
  initImport,
  resetImport,
  resetImportForAllTypes,
  setImportedEntityListItems,
  setSourceTypeFilters,
} = entityListAdministrationSlice.actions;
